export type Lang = 'en' | 'fa'

// Canonical dial string for tel: hrefs. The localized display strings below
// use Persian digits on fa, which browsers can't dial — hrefs must use this.
export const PHONE_E164 = '+982128422073'

export const translations = {
  en: {
    nav: {
      services: 'Services',
      trackRecord: 'Organizations',
      faq: 'FAQ',
      blog: 'Blog',
      cta: 'Start a Campaign →',
    },
    hero: {
      h1a: 'The Intelligence Behind Influence',
      h1b: '',
      h1c: '',
      sub: 'A vast network of influencers across Iran, with over 10,000+ influencers; from brief to performance report in under 48 hours.',
      cta1: 'Start a Campaign',
      cta2: 'See the Proof',
      scroll: 'Scroll down',
    },
    proof: {
      heading: "The First Choice of Iran's Leading Brands",
      aboutBold: 'We turn influencer reach into measurable performance,',
      aboutMuted: 'with data that brings clarity to your decision-making.',
      pts: [
        { label: 'Near-zero client loss', note: 'Retention' },
        { label: '40 people. The output of 200.', note: 'Efficiency' },
        { label: 'Compounding data since 2020', note: 'Experience' },
      ],
      campaignsTitle: '7,100+ Campaigns',
      campaignsSub: 'Delivered',
      enterpriseLabel: 'Enterprise Clients',
      factLabel: 'Trend at a Glance',
    },
    manifesto: {
      eyebrow: 'The Trend Promise',
      headlineParts: {
        prefix: 'We get your message to the right audience: ',
        accent1: 'fastest',
        sep1: ', ',
        accent2: 'cheapest',
        sep2: ', through the ',
        accent3: 'best media',
        suffix: '.',
      },
      cta: {
        lead: 'Ready to put your message everywhere?',
        button: 'Build a Campaign',
      },
    },
    network: {
      section: 'Creator Network',
      headline: '10,000+ creators. 12 categories. 31 provinces.',
      description:
        "From mega-influencers to local micro-creators. Iran's most complete creator network, indexed and ready.",
      categoriesLabel: 'Creator Categories',
      cards: {
        influencer: {
          eyebrow: 'Influencer Marketing',
          title: 'Micro to Mega',
          description:
            'Strategic selection based on cultural relevance and campaign performance (not follower count). Full category coverage with precision execution.',
          tiers: ['Mega', 'Macro', 'Micro', 'Nano'],
        },
        provincial: {
          eyebrow: 'Provincial Micro-Influencer',
          title: '31 Provinces. One Campaign.',
          description:
            'Creators under 200K followers across all 31 Iranian provinces, with local authenticity at national scale.',
          formats: ['Banner Story', 'Selfie Story', 'Serial Story', 'Post'],
        },
        celebrity: {
          eyebrow: 'Celebrity & Public Page',
          title: '2.6M+ Followers',
          description:
            'Full-cycle celebrity collaboration: from negotiation and contract to scenario writing and brand-aligned execution.',
          tiers: ['Mega', 'Celebrity', 'Actor', 'Public Page'],
        },
      },
    },
    problem: {
      section: 'Why Trend?',
      gapEyebrow: 'The Imbalance',
      leftTitle: 'Market Gaps in the Media Landscape',
      leftBody:
        'Users spend 55% of their media time online, yet only 19% of ad budgets reach this space. This gap is where data and measurement matter most; we help brands show up exactly where they need to be.',
      s1val: '55%',
      s1label: 'Media consumption online',
      s2val: '19%',
      s2label: 'Ad budgets going digital',
      gapNum: '36',
      gapLabel: 'Units: The Gap Between Visibility and Impact.',
      rightTitle: "Trend's Solution",
      solutionEyebrow: 'The Fix',
      gapClosed: 'Gap closed',
      rightBody:
        'We have transformed influencer marketing into measurable and actionable data; from visibility to real ROI.',
      bullets: [
        'Over 10,000 influencers, managed and coordinated seamlessly.',
        'Data and insights for more confident decisions.',
        'Every campaign generates new insights for your next decision.',
      ],
      tablistLabel: 'The three levers',
      tabLabels: ['Creator network', 'Trusted measurement', 'Compounding data'],
    },
    measurement: {
      section: 'How We Measure Performance',
      headingPre: 'Campaigns that do more than just get seen—',
      headingAccent: 'they deliver results.',
      sub: 'We turn campaigns into numbers: transparent, measurable, and reportable.',
      chart: {
        cost: 'Cost',
        impressions: 'Impressions',
        timeline: 'Campaign Journey',
        down: 'Lower',
        up: 'Higher',
        caption: 'Example of Campaign Optimization',
      },
    },
    services: {
      section: 'Services',
      heading: 'Everything a brand needs for an effective social media presence.',
      sub: 'Six Capabilities, One Coordinated Campaign Journey.',
    },
    ads: {
      formatsLabel: 'Ad Formats',
    },
    channels: {
      section: 'Active Platforms',
      heading: 'From a single brief to every channel in Iran.',
      sub: 'One brief. Every major platform and Iranian messenger. Unified reporting.',
      h1: 'Provinces covered',
      h2: 'Active platforms',
      h3: 'WhatsApp channels',
      h4: 'Messenger channels',
      social: 'Social & Video',
      messengers: 'Iranian Messengers',
      provinceTitle: '31-Province Micro-Influencer Coverage',
      provinceSub:
        'Local creators under 200K followers across all 31 provinces. Story, Post, and Telegram. Real local voice at national scale.',
    },
    track: {
      section: 'Organizations',
      brandsHeading: 'The brands that bet on Trend.',
      brandsSubline: '120+ brands across 7 industries trust us with their media.',
    },
    faq: {
      section: 'FAQ',
      heading: 'Frequently Asked Questions for Executives',
      sub: 'The answers you need before the budget meeting.',
    },
    contact: {
      section: 'Contact',
      heading: 'Your next campaign can perform even better.',
      sub: 'Share your current goals, audience, and media with us; we will suggest the most effective path for your campaign execution.',
      name: 'Full Name',
      email: 'Work Email',
      company: 'Company',
      budget: 'Campaign Budget Range',
      budgetOpts: ['Under $10K', '$10K – $50K', '$50K – $200K', '$200K+'],
      message: 'Tell us about your campaign goals...',
      submit: 'Send Message →',
      submitting: 'Sending...',
      successTitle: 'Message received',
      successSub: "We'll be in touch within 24 hours.",
      error: 'Something went wrong. Please try again or email us directly.',
      address: 'No. 3, Mozaffari Alley, Vaziri Pour St, Mirdamad Blvd, Tehran',
      contactEmail: 'contact@trendmedia.ai',
      contactPhone: '021-28422073',
      errors: {
        nameRequired: 'Please enter your name',
        nameTooLong: 'Name must be 100 characters or fewer',
        emailRequired: 'Please enter your work email',
        emailInvalid: 'That email address looks off — check the format',
        companyRequired: 'Please tell us which company you work for',
        companyTooLong: 'Company name must be 200 characters or fewer',
        messageTooLong: 'Message must be 5000 characters or fewer',
        budgetInvalid: 'Pick one of the listed budget ranges',
        attachmentTooLarge: 'File is larger than 10 MB',
        attachmentTypeInvalid: 'Allowed types: PDF, DOC, DOCX, JPG, PNG, WEBP',
      },
      attachmentLabel: 'Attach a brief (optional, PDF/DOC/image)',
      attachmentChange: 'Replace file',
      attachmentRemove: 'Remove',
    },
    footer: {
      tagline: 'The Intelligence Behind Influence',
      rights: 'All rights reserved.',
      sub: 'The Intelligence Behind Influence',
      links: ['Services', 'Organizations', 'FAQ', 'Contact', 'Blog'],
      navHeading: 'Explore',
      contactHeading: 'Get in touch',
      ctaText: 'Ready when you are',
      ctaHeadline: 'Are you ready to run your next campaign smarter?',
      ctaButton: 'Start a Collaboration',
      backToTop: 'Back to top',
    },
  },

  fa: {
    nav: {
      services: 'خدمات',
      trackRecord: 'سازمان‌ها',
      faq: 'سوالات',
      blog: 'بلاگ',
      cta: 'شروع کمپین ←',
    },
    hero: {
      h1a: 'تفکر هوشمند، تاثیرگذاری هدفمند',
      h1b: '',
      h1c: '',
      sub: 'شبکه‌ای گسترده از اینفلوئنسرهای سراسر ایران، با بیش از +۱۰ هزار اینفلوئنسر؛ از بریف تا گزارش عملکرد، در کمتر از ۴۸ ساعت.',
      cta1: 'شروع کمپین',
      cta2: 'اثباتش را ببینید',
      scroll: 'پایین بروید',
    },
    proof: {
      heading: 'انتخاب اول بزرگترین برندهای ایران',
      aboutBold: 'تلفیق تفکر هوشمند و داده‌ها؛ برای تبدیل قدرت اینفلوئنسرها به نتایج واقعی.',
      aboutMuted:
        'ما دسترسی اینفلوئنسرها را به عملکردی قابل‌اندازه‌گیری تبدیل می‌کنیم؛ با داده‌هایی که تصمیم‌گیری را شفاف‌تر می‌کنند.',
      pts: [
        { label: 'نزدیک به صفر ریزش مشتری', note: 'نرخ ماندگاری' },
        { label: '۴۰ نفر، با خروجی کار ۲۰۰ نفر', note: 'بهره‌وری' },
        { label: 'انباشت داده از سال ۱۳۹۹', note: 'تجربه' },
      ],
      campaignsTitle: '۷،۱۰۰+ کمپین',
      campaignsSub: 'اجرا شده',
      enterpriseLabel: 'مشتری سازمانی',
      factLabel: 'ترند در یک نگاه',
    },
    manifesto: {
      eyebrow: 'تعهد ترند',
      headlineParts: {
        prefix: 'پیام شما را ',
        accent1: 'سریع‌تر',
        sep1: '، ',
        accent2: 'ارزان‌تر',
        sep2: ' و از ',
        accent3: 'بهترین رسانه‌ها',
        suffix: ' به مخاطب درست می‌رسانیم.',
      },
      cta: {
        lead: 'آماده‌اید پیامتان را پخش کنیم؟',
        button: 'ساخت کمپین',
      },
    },
    network: {
      section: 'شبکه کریتور',
      headline: '۱۰،۰۰۰+ کریتور. ۱۲ دسته‌بندی. ۳۱ استان.',
      description:
        'از مگا اینفلوئنسرها تا میکرو کریتورهای محلی. کامل‌ترین شبکه کریتور ایران، ایندکس‌شده و آماده.',
      categoriesLabel: 'دسته‌بندی‌های کریتور',
      cards: {
        influencer: {
          eyebrow: 'اینفلوئنسر مارکتینگ',
          title: 'از میکرو تا مگا',
          description:
            'انتخاب استراتژیک بر اساس ارتباط فرهنگی و عملکرد کمپین (نه تعداد فالوور). پوشش تمام دسته‌بندی‌ها با اجرای دقیق.',
          tiers: ['مگا', 'ماکرو', 'میکرو', 'نانو'],
        },
        provincial: {
          eyebrow: 'میکرو اینفلوئنسر استانی',
          title: '۳۱ استان. یک کمپین.',
          description:
            'کریتورهای زیر ۲۰۰ هزار فالوور در تمام ۳۱ استان ایران؛ اصالت محلی در مقیاس ملی.',
          formats: ['استوری بنر', 'استوری سلفی', 'استوری سریالی', 'پست'],
        },
        celebrity: {
          eyebrow: 'سلبریتی و پیج عمومی',
          title: 'بیش از ۲.۶ میلیون فالوور',
          description:
            'همکاری با سلبریتی‌های ایرانی: از مذاکره و قرارداد تا نگارش سناریو و اجرای هماهنگ‌شده با دیدگاه برند.',
          tiers: ['مگا', 'سلبریتی', 'بازیگر', 'پیج عمومی'],
        },
      },
    },
    problem: {
      section: 'چرا ترند .',
      gapEyebrow: 'عدم توازن',
      leftTitle: 'شکاف بازار در دنیای رسانه',
      leftBody:
        'کاربران ۵۵٪ از زمان خود را در فضای آنلاین می‌گذرانند، اما سهم این فضا از بودجه‌های تبلیغاتی تنها ۱۹٪ است. در این نقطه طلایی، ما با رویکردی داده‌محور مسیر دیده‌شدن برند شما را هموار می‌کنیم.',
      s1val: '۵۵٪',
      s1label: 'مصرف رسانه آنلاین',
      s2val: '۱۹٪',
      s2label: 'بودجه تبلیغات دیجیتال',
      gapNum: '۳۶',
      gapLabel: 'واحد فاصله بین دیده‌شدن و اثرگذاری.',
      rightTitle: 'راه‌حل ترند',
      solutionEyebrow: 'راه‌حل',
      gapClosed: 'شکاف پر شد',
      rightBody:
        'ما اینفلوئنسر مارکتینگ را به اعداد و ارقام تبدیل می‌کنیم؛ مسیری شفاف از دیده‌شدن تا بازدهی واقعی.',
      bullets: [
        'بیش از ۱۰ هزار اینفلوئنسر، با مدیریت و هماهنگی یکپارچه.',
        'داده‌ها و گزارش‌هایی برای تصمیم‌های مطمئن‌تر.',
        'هر کمپین، بینش تازه‌ای برای تصمیم بعدی می‌سازد.',
      ],
      tablistLabel: 'سه اهرم',
      tabLabels: ['شبکه کریتور', 'اندازه‌گیری معتبر', 'داده انباشته'],
    },
    measurement: {
      section: 'چطور عملکرد را می‌سنجیم',
      headingPre: 'عبور از معیارهای نمایشی؛ ما کمپین‌هایی می‌سازیم که ',
      headingAccent: 'نتایج قابل‌اندازه‌گیری رقم می‌زنند.',
      sub: 'ما کمپین‌ها را به عدد تبدیل می‌کنیم؛ شفاف، قابل‌اندازه‌گیری و قابل‌گزارش.',
      chart: {
        cost: 'هزینه',
        impressions: 'ایمپرشن',
        timeline: 'مسیر کمپین',
        down: 'کاهش',
        up: 'افزایش',
        caption: 'نمونه‌ای از بهینه‌سازی عملکرد در طول کمپین',
      },
    },
    services: {
      section: 'خدمات',
      heading: 'مسیری یکپارچه برای حضور هوشمندانه برند شما در فضای دیجیتال.',
      sub: 'شش قابلیت، یک مسیر هماهنگ برای اجرای کمپین',
    },
    ads: {
      formatsLabel: 'فرمت‌های تبلیغاتی',
    },
    channels: {
      section: 'پلتفرم‌های فعال',
      heading: 'از یک بریف تا تمام کانال‌های ایران.',
      sub: 'یک بریف. تمام پلتفرم‌های اصلی و پیام‌رسان‌های ایرانی. گزارش‌دهی یکپارچه.',
      h1: 'استان تحت پوشش',
      h2: 'پلتفرم فعال',
      h3: 'کانال واتساپ',
      h4: 'کانال پیام‌رسان',
      social: 'شبکه‌های اجتماعی و ویدیو',
      messengers: 'پیام‌رسان‌های ایرانی',
      provinceTitle: 'پوشش میکرو اینفلوئنسر در ۳۱ استان',
      provinceSub:
        'کریتورهای محلی زیر ۲۰۰ هزار فالوور در تمام ۳۱ استان. استوری، پست و تلگرام. صدای محلی واقعی در مقیاس ملی.',
    },
    track: {
      section: 'سازمان‌ها',
      brandsHeading: 'برندهایی که روی ترند شرط بستند.',
      brandsSubline: 'بیش از ۱۲۰ برند در ۷ صنعت، رسانه‌شان را به ما سپرده‌اند.',
    },
    faq: {
      section: 'سوالات (FAQ)',
      heading: 'پاسخ به سوالات پرتکرار مدیران',
      sub: 'جواب‌هایی که پیش از جلسه بودجه به آن نیاز دارید.',
    },
    contact: {
      section: 'تماس با ما',
      heading: 'کمپین بعدی شما، می‌تواند دقیق‌تر عمل کند.',
      sub: 'اهداف، مخاطبان و رسانه‌های فعلی‌تان را با ما به اشتراک بگذارید؛ ما مناسب‌ترین مسیر اجرای کمپین را پیشنهاد می‌دهیم.',
      name: 'نام و نام خانوادگی',
      email: 'ایمیل کاری',
      company: 'شرکت',
      budget: 'محدوده بودجه کمپین',
      budgetOpts: [
        'زیر ۵۰۰ میلیون تومان',
        '۵۰۰ میلیون تا ۲ میلیارد تومان',
        '۲ تا ۱۰ میلیارد تومان',
        'بیش از ۱۰ میلیارد تومان',
      ],
      message: 'درباره اهداف کمپین خود بگویید...',
      submit: 'ارسال پیام ←',
      submitting: 'در حال ارسال...',
      successTitle: 'پیام دریافت شد',
      successSub: 'ظرف ۲۴ ساعت با شما در تماس خواهیم بود.',
      error: 'مشکلی پیش آمد. دوباره امتحان کنید یا مستقیم ایمیل بزنید.',
      address: 'خیابان میرداماد، خیابان وزیری‌پور، کوچه مظفری، پلاک ۳',
      contactEmail: 'contact@trendmedia.ai',
      contactPhone: '۰۲۱-۲۸۴۲۲۰۷۳',
      errors: {
        nameRequired: 'نام خود را وارد کنید',
        nameTooLong: 'نام باید حداکثر ۱۰۰ کاراکتر باشد',
        emailRequired: 'ایمیل کاری خود را وارد کنید',
        emailInvalid: 'فرمت ایمیل صحیح نیست',
        companyRequired: 'نام شرکت خود را وارد کنید',
        companyTooLong: 'نام شرکت باید حداکثر ۲۰۰ کاراکتر باشد',
        messageTooLong: 'پیام باید حداکثر ۵۰۰۰ کاراکتر باشد',
        budgetInvalid: 'یکی از محدوده‌های فهرست‌شده را انتخاب کنید',
        attachmentTooLarge: 'حجم فایل بیشتر از ۱۰ مگابایت است',
        attachmentTypeInvalid: 'فرمت‌های مجاز: PDF، DOC، DOCX، JPG، PNG، WEBP',
      },
      attachmentLabel: 'پیوست بریف (اختیاری، PDF/DOC/تصویر)',
      attachmentChange: 'تغییر فایل',
      attachmentRemove: 'حذف',
    },
    footer: {
      tagline: 'تفکر هوشمند، تاثیرگذاری هدفمند',
      rights: 'تمامی حقوق محفوظ است.',
      sub: 'تفکر هوشمند، تاثیرگذاری هدفمند',
      links: ['خدمات ما', 'سازمان‌ها', 'سوالات متداول', 'تماس با ما', 'بلاگ'],
      navHeading: 'صفحه اصلی',
      contactHeading: 'تماس با ما',
      ctaText: 'هر وقت آماده بودید',
      ctaHeadline: 'آماده‌اید کمپین بعدی را هوشمندتر اجرا کنیم؟',
      ctaButton: 'شروع همکاری',
      backToTop: 'بازگشت به بالا',
    },
  },
} as const

export type T = typeof translations.en
